<template>
  <div>
    <!-- Encabezado con selector de tipo de documento -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div class="flex items-center">
        <button 
          class="mr-3 p-2.5 text-white bg-teal-800 hover:bg-teal-900 rounded-lg transition-colors" 
          @click="back()"
          title="Volver atrás"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="text-3xl font-bold text-gray-900">
            {{ form.type === 'invoice' ? 'Crear Factura' : 'Crear Nota de Entrega' }}
          </h1>
          <p class="text-sm text-gray-500">
            {{ form.type === 'invoice' ? 'Emisión de factura comercial' : 'Emisión de nota de entrega para despacho' }}
          </p>
        </div>
      </div>

      <!-- Toggle selector de tipo de documento -->
      <div class="flex items-center p-1 bg-gray-200 rounded-xl shadow-inner">
        <button
          type="button"
          @click="form.type = 'delivery_note'"
          :class="[
            'px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all',
            form.type === 'delivery_note'
              ? 'bg-white text-teal-800 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          <i class="fa fa-file-lines"></i>
          Nota de entrega
        </button>

        <button
          type="button"
          @click="form.type = 'invoice'"
          :class="[
            'px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all',
            form.type === 'invoice'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          <i class="fa fa-file-invoice"></i>
          Factura
        </button>
      </div>
    </div>

    <Head :title="form.type === 'invoice' ? 'Crear Factura' : 'Crear Nota de Entrega'" />

    <!-- Sección de cliente -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6">
        <find-or-create-client @addClient="takeClient" />
        
        <!-- Formulario de cliente -->
        <div v-if="showClientCreate" class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <text-input 
            v-model="form.client.name"
            label="Nombre y Apellido"
            placeholder="Ingrese el nombre completo"
          />

          <text-input
            v-model="form.client.identification" 
            label="Alias o Cédula"
            placeholder="Ingrese identificación"
          />

          <text-input
            v-model="form.client.phone"
            label="Teléfono"
            placeholder="Ingrese número de contacto" 
          />

          <text-input
            v-model="form.client.address"
            label="Dirección"
            placeholder="Ingrese dirección completa"
          />

          <text-input
            v-model="form.client.balance"
            label="Monto adeudado" 
            disabled
          />

          <div>
            <label class="block text-gray-700 mb-2">Tipo de cliente</label>
            <select 
              v-model="form.client.typeClient"
              class="form-select w-full rounded-lg border-gray-300"
            >
              <option value="">Seleccionar tipo</option>
              <option value="freelancer">Freelancer</option>
              <option value="spot">SPOT</option>
              <option value="a">A</option>
              <option value="b">B</option>
              <option value="c">C</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección de productos -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6">
        <div class="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
          <div class="flex gap-2">
            <button 
              class="btn-blue flex items-center"
              @click="listProducts = !listProducts"
              title="Ver lista de productos"
            >
              <i class="fa fa-list mr-2"></i>
              Lista
            </button>

            <button 
              class="btn-blue flex items-center"
              @click="addQuickProduct()"
              title="Agregar producto rápido"
            >
              <i class="fa fa-plus mr-2"></i>
              Producto rápido
            </button>
          </div>

          <search-filter
            v-model="form.search.terms"
            class="w-full sm:max-w-md"
            @reset="form.search.terms = ''"
          >
          </search-filter>
        </div>

        <!-- Lista de productos -->
        <div v-if="listProducts" class="mb-6">
          <div class="overflow-x-auto rounded-lg border">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="p-3 text-left">Código</th>
                  <th class="p-3 text-left">Producto</th>
                  <th class="p-3 text-center">Disponibles</th>
                  <th 
                    v-for="group in groups" 
                    :key="group.id" 
                    class="p-3 text-right"
                  >
                    Precio {{ group.name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="product in products.data" 
                  :key="product.id"
                  class="hover:bg-gray-50 cursor-pointer border-t"
                  @click="selectProduct(product)"
                >
                  <td class="p-3">{{ product.code }}</td>
                  <td class="p-3">{{ product.name }}</td>
                  <td class="p-3 text-center">{{ product.quantity }}</td>
                  <td 
                    v-for="group in groups" 
                    :key="group.id" 
                    class="p-3 text-right"
                  >
                    ${{ getGroupPrice(product.prices, group.id) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Carrito de compras -->
        <div class="overflow-x-auto rounded-lg border">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="p-3 text-left">Código</th>
                <th class="p-3 text-left">Producto</th>
                <th class="p-3 text-center">Disponible</th>
                <th class="p-3 text-center">Cantidad</th>
                <th class="p-3 text-right">Precio</th>
                <th class="p-3 text-right">Total</th>
                <th class="p-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(product, index) in form.products" :key="product.id" class="border-t">
                <td class="p-3">
                  <text-input 
                    v-if="product.quick"
                    v-model="form.products[index].code"
                    placeholder="Código"
                  />
                  <span v-else>{{ product.code }}</span>
                </td>
                <td class="p-3">
                  <text-input
                    v-if="product.quick"
                    v-model="form.products[index].name"
                    placeholder="Nombre"
                  />
                  <div v-else>
                    <span>{{ product.name }}</span>
                    <div v-if="product.require_note" class="mt-2">
                      <input 
                        type="text" 
                        v-model="form.products[index].note" 
                        class="w-full p-2 border border-gray-300 rounded text-sm" 
                        placeholder="Nota requerida *"
                        required
                      />
                    </div>
                  </div>
                </td>
                <td class="p-3 text-center">{{ product.stock }}</td>
                <td class="p-3">
                  <text-input
                    v-model="form.products[index].quantity"
                    type="number"
                    :max="product.stock"
                    class="w-20 text-center"
                  />
                </td>
                <td class="p-3">
                  <select 
                    v-model="form.products[index].priceType"
                    @change="onPriceTypeChange(index)"
                    class="w-full rounded-lg border-gray-300 mb-2"
                  >
                    <option 
                      v-for="group in groups" 
                      :key="group.id" 
                      :value="'group-'+group.id"
                    >
                      {{ group.name }} - ${{ getGroupPrice(product.prices, group.id) }}
                    </option>
                    <option v-if="auth.user.role !== 'sales'" value="custom">Personalizado</option>
                  </select>

                  <text-input
                    v-model="form.products[index].price"
                    type="number"
                    :disabled="form.products[index].priceType !== 'custom' || (!product.quick && auth.user.role == 'sales')"
                    class="w-24 text-right"
                  />
                </td>
                <td class="p-3 text-right">
                  ${{ (form.products[index].price * product.quantity).toFixed(2) }}
                </td>
                <td class="p-3 text-center">
                  <button
                    @click="removeProduct(product)"
                    class="p-2 text-white bg-red-500 hover:bg-red-600 rounded-full transition-colors"
                    title="Eliminar producto"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-50">
              <tr>
                <td colspan="5" class="p-3 text-right">Subtotal:</td>
                <td class="p-3 text-right">
                  <div class="font-medium">${{ getSubtotal() }}</div>
                </td>
                <td></td>
              </tr>
              <tr v-if="getDiscountAmount() > 0">
                <td colspan="5" class="p-3 text-right text-red-600">
                  Descuento 
                  <span v-if="form.discount.type === 'percentage'">({{ form.discount.value }}%)</span>:
                </td>
                <td class="p-3 text-right text-red-600">
                  <div>-${{ getDiscountAmount() }}</div>
                </td>
                <td></td>
              </tr>
              <tr>
                <td colspan="5" class="p-3 text-right font-bold">Total:</td>
                <td class="p-3 text-right">
                  <div class="text-xl font-bold">${{ getTotal() }}</div>
                  <div class="text-sm text-gray-600">Bs. {{ (getTotal() * form.change).toFixed(2) }}</div>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Sección de descuento -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6">
        <h3 class="text-lg font-semibold mb-4">Aplicar descuento</h3>
        
        <div class="flex flex-wrap items-end gap-4">
          <div>
            <label class="block text-gray-700 mb-2">Tipo de descuento</label>
            <select 
              v-model="form.discount.type"
              class="form-select rounded-lg border-gray-300"
            >
              <option value="percentage">Porcentaje (%)</option>
              <option value="amount">Monto fijo ($)</option>
            </select>
          </div>
          
          <text-input
            v-model="form.discount.value"
            :label="form.discount.type === 'percentage' ? 'Porcentaje (%)' : 'Monto ($)'"
            type="number"
            :step="form.discount.type === 'percentage' ? '1' : '0.01'"
            :min="0"
            :max="form.discount.type === 'percentage' ? 100 : null"
            class="w-32"
          />
          
          <div class="text-gray-600">
            <span class="font-medium">Descuento aplicado:</span> 
            <span class="text-red-600 font-bold">${{ getDiscountAmount() }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Opciones de crédito -->
    <div class="bg-blue-100 rounded-lg p-4 mb-6">
      <label class="inline-flex items-center cursor-pointer">
        <input 
          v-model="form.isCredit"
          type="checkbox"
          class="form-checkbox h-5 w-5 text-blue-600"
        >
        <span class="ml-2 text-lg font-semibold">Venta a crédito</span>
      </label>
    </div>

    <!-- Detalles adicionales -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6 space-y-6">
        <!-- Campo para ingresar el número correlativo de la orden -->
        <!-- Campo para ingresar el número correlativo de la orden -->
        <text-input
          v-model="form.number_order"
          label="Correlativo de Pedido / Nota de Entrega (Opcional)"
          :placeholder="`Automático (Siguiente: ${nextNumberOrder || 'automático'})`"
        />

        <!-- Campo para ingresar el número / correlativo de la factura -->
        <div v-if="form.type === 'invoice'" class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <text-input
            v-model="form.invoice_number"
            label="Número / Correlativo de Factura (Opcional)"
            :placeholder="`Automático (Siguiente: ${nextInvoiceNumber || 'automático'})`"
          />
          <p class="text-xs text-blue-600 mt-1">
            <i class="fa fa-info-circle mr-1"></i>
            Si deja este campo vacío, se asignará automáticamente el correlativo ({{ nextInvoiceNumber || 'automático' }}).
          </p>
        </div>

        <text-input
          v-model="form.date"
          label="Fecha y hora"
          type="datetime-local"
          required
        />

        <text-input
          v-model="form.change"
          label="Tasa de cambio"
          type="number"
          step="0.01"
        />

        <text-area-input
          v-model="form.comentary"
          label="Observaciones"
          placeholder="Ingrese observaciones importantes"
          required
        />
      </div>
    </div>

    <!-- Botón de generar venta / nota de entrega / factura -->
    <div class="flex justify-end">
      <button 
        @click="generateOrder"
        :class="[
          'text-lg px-8 py-3 rounded-lg font-bold text-white transition-all shadow-md flex items-center',
          form.type === 'invoice' ? 'bg-blue-700 hover:bg-blue-800' : 'bg-teal-700 hover:bg-teal-800'
        ]"
      >
        <i class="fa fa-check mr-2"></i>
        {{ form.type === 'invoice' ? 'Generar Factura' : 'Generar Nota de Entrega' }}
      </button>
    </div>
  </div>
</template>

<script>
import Layout from '../../Shared/Layout.vue'
import TextInput from '@/Shared/TextInput'
import pickBy from 'lodash/pickBy'
import { Head } from '@inertiajs/inertia-vue3'
import SelectInput from '@/Shared/SelectInput.vue'
import FindOrCreateClient from '../../Shared/FindOrCreateClient.vue'
import TextAreaInput from '@/Shared/TextAreaInput'
import SearchFilter from '@/Shared/SearchFilter.vue'

export default {
  components: {
    SelectInput,
    TextInput,
    Head,
    FindOrCreateClient,
    TextAreaInput,
    SearchFilter
  },

  layout: Layout,

  props: {
    products: Object,
    groups: Object,
    client: Object,
    brands: Object,
    parts: Object,
    models: Object,
    deposits: Object,
    regions: Object,
    change: Number,
    auth: Object,
    documentType: {
      type: String,
      default: 'delivery_note'
    },
    nextNumberOrder: String,
    nextInvoiceNumber: String
  },

  data() {
    return {
      getQuantity(inventory) {
        return inventory.reduce((total, inv) => total + inv.quantity, 0)
      },

      getTotal() {
        return this.form.products.reduce((total, product) => {
          return total + (product.price * product.quantity)
        }, 0).toFixed(2)
      },

      selected: null,
      options: ['list', 'of', 'options'],
      
      form: this.$inertia.form({
        type: this.documentType || 'delivery_note',
        invoice_number: '',
        date: '',
        search: {
          terms: '',
          deposit: '',
          brand_id: '',
          part_id: '',
          truck_model_id: '',
          region_id: '',
        },
        isCredit: false,
        number_order: null,
        client: {
          name: '',
          email: '',
          cedula: '',
          telefono: '',
          address: '',
          identification: '',
          balance: 0,
          typeClient: '',
        },
        discount: {
          type: 'percentage', // 'percentage' or 'amount'
          value: 0
        },
        comentary: '',
        products: [],
        method: '',
        referencePay: '',
        change: this.change,
        estimate: true,
        order_buy: ''
      }),

      listProducts: false,
      showClientCreate: false,
      textSearch: 'Buscar cliente',
    }
  },

  watch: {
    'form.search.terms': 'onSearchProductsChange',
    'form.search.deposit': 'onSearchProductsChange', 
    'form.search.brand_id': 'onSearchProductsChange',
    'form.search.part_id': 'onSearchProductsChange',
    'form.search.truck_model_id': 'onSearchProductsChange',
    'form.search.region_id': 'onSearchProductsChange'
  },

  mounted() {
    this.form.date = new Date().toISOString().substr(0,10)
  },

  methods: {
    getGroupPrice(prices, groupId) {
      const match = prices.find(p => p.group_id === groupId)
      return match ? match.price : '-'
    },
    takeClient(client) {
      this.form.client = client
    },

    getDeposits(deposits) {
      return deposits
    },

    getSubtotal() {
      return this.form.products.reduce((total, product) => {
        return total + (parseFloat(product.price) * parseFloat(product.quantity))
      }, 0).toFixed(2)
    },

    getDiscountAmount() {
      const subtotal = parseFloat(this.getSubtotal());
      if (this.form.discount.type === 'percentage') {
        return ((subtotal * parseFloat(this.form.discount.value)) / 100).toFixed(2);
      } else {
        return Math.min(parseFloat(this.form.discount.value), subtotal).toFixed(2);
      }
    },

    getTotal() {
      const subtotal = parseFloat(this.getSubtotal());
      const discount = parseFloat(this.getDiscountAmount());
      return (subtotal - discount).toFixed(2);
    },

    back() {
      window.history.back()
    },

    addQuickProduct() {
      this.form.products.push({
        id: 'quick',
        code: '',
        name: '',
        deposit: '',
        quantity: 1,
        price: 0,
        quick: true
      })
    },

    getProductStock(inventory, deposito) {
      const found = inventory.find(inv => inv.deposit.id == deposito)
      return found ? found.quantity : ''
    },

    selectProduct(product) {
      if (product.quantity <= 0) {
        return alert('No hay stock disponible de este producto')
      }

      const exists = this.form.products.some(p => p.id === product.id)
      
      if (!exists) {
        const defaultPrice = product?.prices?.length ? product.prices[0].price : 0
        const defaultGroupId = product?.prices?.length ? product.prices[0].group_id : null
        this.form.products.push({
          ...product,
          stock: product.quantity,
          quantity: 1,
          price: defaultPrice,
          priceType: defaultGroupId ? `group-${defaultGroupId}` : 'custom',
          quick: false
        })
      }
    },

    removeProduct(product) {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.form.products = this.form.products.filter(p => p.id !== product.id)
      }
    },

    findClient() {
      this.$inertia.get('/orders/create', pickBy(this.form), { preserveState: true })
      this.textSearch = 'Buscando...'

      setTimeout(() => {
        if (this.client) {
          this.textSearch = 'Buscar cliente'
          this.showClientCreate = true
          this.form.client = {
            name: this.client.name,
            email: this.client.email,
            phone: this.client.phone,
            address: this.client.address,
            identification: this.client.identification,
            balance: this.client.balance,
            typeClient: this.client.typeClient,
            companyName: this.client.companyName,
          }
        } else {
          this.textSearch = 'Buscar cliente'
          alert('No se encontró el cliente. Puede crear uno nuevo en el formulario.')
          this.showClientCreate = true
        }
      }, 1500)
    },

    generateOrder() {
      if (this.form.products.length === 0) {
        return alert("Debe agregar productos a la orden");
      }
      
      const missingNote = this.form.products.find(p => p.require_note && (!p.note || p.note.trim() === ''));
      if (missingNote) {
        return alert(`El producto ${missingNote.name} requiere una nota obligatoria.`);
      }

      this.form.post('/orders')
    },

    onSearchProductsChange() {
      this.$inertia.get('/orders/create', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        }
      )
    },

    onSelectedProduct(consultant) {
      console.log(consultant)
    },

    updateSelected(newSelected) {
      this.selectProduct = newSelected
    },

    onPriceTypeChange(index) {
      const product = this.form.products[index]
      if (!product) return

      if (product.priceType === 'custom') {
        // For sales role, enforce group price if they somehow end up on custom
        if (this.auth.user.role === 'sales') {
          const fallbackGroupId = product?.prices?.length ? product.prices[0].group_id : null
          product.priceType = fallbackGroupId ? `group-${fallbackGroupId}` : null
        }
        return
      }

      if (product.priceType && product.priceType.startsWith('group-')) {
        const groupId = parseInt(product.priceType.replace('group-', ''))
        const price = this.getGroupPrice(product.prices, groupId)
        if (price !== '-') {
          this.form.products[index].price = price
        }
      }
    },
  },
}
</script>
